/**
 * User Preferences Component
 * Allows users to configure their personal preferences for date/time/number formatting
 *
 * @author      Aslam Bari
 * @date        Nov, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Container, Row, Col, Alert } from 'react-bootstrap';
import { FaSave, FaCog } from 'react-icons/fa';
import jwt_decode from 'jwt-decode';
import DataApi from '../api/DataApi';
import { getUserPreferences } from '../utils/formatUtils';

const UserPreferences = () => {
  const [preferences, setPreferences] = useState({
    time_zone: 'UTC',
    locale: 'en-US',
    date_format: 'DD/MM/YYYY',
    time_format: 'HH:mm'
  });

  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ show: false, type: '', text: '' });

  useEffect(() => {
    // Load current preferences from token
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwt_decode(token);
        setUserId(decoded.id);

        const currentPrefs = getUserPreferences();
        setPreferences(currentPrefs);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  }, []);

  const handleChange = (field, value) => {
    setPreferences({
      ...preferences,
      [field]: value
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ show: false, type: '', text: '' });

    try {
      const dataApi = new DataApi('user');

      await dataApi.put(`/${userId}`, {
        time_zone: preferences.time_zone,
        locale: preferences.locale,
        date_format: preferences.date_format,
        time_format: preferences.time_format
      });

      setMessage({
        show: true,
        type: 'success',
        text: 'Preferences saved successfully! Please log out and log back in for changes to take effect.'
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({
        show: true,
        type: 'danger',
        text: 'Error saving preferences. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card>
            <Card.Header className="d-flex align-items-center">
              <FaCog className="me-2" />
              <h5 className="mb-0">User Preferences</h5>
            </Card.Header>
            <Card.Body>
              {message.show && (
                <Alert variant={message.type} onClose={() => setMessage({ show: false, type: '', text: '' })} dismissible>
                  {message.text}
                </Alert>
              )}

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Timezone</Form.Label>
                  <Form.Select
                    value={preferences.time_zone}
                    onChange={(e) => handleChange('time_zone', e.target.value)}
                  >
                    <option value="UTC">UTC (Coordinated Universal Time)</option>
                    <option value="Asia/Kolkata">Asia/Kolkata (Indian Standard Time)</option>
                    <option value="America/New_York">America/New_York (Eastern Time)</option>
                    <option value="America/Chicago">America/Chicago (Central Time)</option>
                    <option value="America/Denver">America/Denver (Mountain Time)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (Pacific Time)</option>
                    <option value="Europe/London">Europe/London (GMT/BST)</option>
                    <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                    <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                    <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select your timezone. All dates and times will be displayed in this timezone.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Date Format</Form.Label>
                  <Form.Select
                    value={preferences.date_format}
                    onChange={(e) => handleChange('date_format', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
                    <option value="DD-MMM-YYYY">DD-MMM-YYYY (31-Dec-2024)</option>
                    <option value="MMM DD, YYYY">MMM DD, YYYY (Dec 31, 2024)</option>
                    <option value="MMMM DD, YYYY">MMMM DD, YYYY (December 31, 2024)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select how dates should be displayed throughout the application.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Time Format</Form.Label>
                  <Form.Select
                    value={preferences.time_format}
                    onChange={(e) => handleChange('time_format', e.target.value)}
                  >
                    <option value="HH:mm">24-hour (14:30)</option>
                    <option value="HH:mm:ss">24-hour with seconds (14:30:45)</option>
                    <option value="hh:mm A">12-hour (02:30 PM)</option>
                    <option value="hh:mm:ss A">12-hour with seconds (02:30:45 PM)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select how times should be displayed.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Locale (Language & Number Format)</Form.Label>
                  <Form.Select
                    value={preferences.locale}
                    onChange={(e) => handleChange('locale', e.target.value)}
                  >
                    <option value="en-US">English (US) - Numbers: 1,234.56</option>
                    <option value="en-IN">English (India) - Numbers: 1,23,456.78</option>
                    <option value="en-GB">English (UK) - Numbers: 1,234.56</option>
                    <option value="de-DE">German - Numbers: 1.234,56</option>
                    <option value="fr-FR">French - Numbers: 1 234,56</option>
                    <option value="es-ES">Spanish - Numbers: 1.234,56</option>
                    <option value="it-IT">Italian - Numbers: 1.234,56</option>
                    <option value="ja-JP">Japanese - Numbers: 1,234.56</option>
                    <option value="zh-CN">Chinese (Simplified) - Numbers: 1,234.56</option>
                    <option value="pt-BR">Portuguese (Brazil) - Numbers: 1.234,56</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select your preferred language and regional format. This controls how numbers, currency, and dates are formatted.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    <FaSave className="me-2" />
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="mt-4">
            <Card.Header>
              <h6 className="mb-0">Preview</h6>
            </Card.Header>
            <Card.Body>
              <p><strong>Current Date/Time:</strong> {new Date().toLocaleString()}</p>
              <p><strong>Sample Number:</strong> {(1234567.89).toLocaleString(preferences.locale)}</p>
              <p><strong>Sample Currency:</strong> {new Intl.NumberFormat(preferences.locale, { style: 'currency', currency: preferences.locale === 'en-IN' ? 'INR' : preferences.locale === 'de-DE' || preferences.locale === 'fr-FR' || preferences.locale === 'es-ES' || preferences.locale === 'it-IT' ? 'EUR' : preferences.locale === 'ja-JP' ? 'JPY' : preferences.locale === 'zh-CN' ? 'CNY' : preferences.locale === 'pt-BR' ? 'BRL' : 'USD' }).format(1234567.89)}</p>
              <p className="mb-0 text-muted">
                <small>
                  Note: To see the changes reflected throughout the application, you must log out and log back in after saving your preferences.
                </small>
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserPreferences;
